import { useState } from 'react'
import { CreditCard, Check, Crown, Calendar, ArrowUp, ArrowDown, AlertCircle, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { subscriptionApi, authApi } from '@/services/api'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export function DashboardSubscription() {
  const queryClient = useQueryClient()
  const [billingCycle, setBillingCycle] = useState('monthly')
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [resubscribeDialogOpen, setResubscribeDialogOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [resubscribeLoading, setResubscribeLoading] = useState(false)

  // Fetch current subscription
  const { data: subscriptionData, isLoading: subLoading, refetch } = useQuery({
    queryKey: ['currentSubscription'],
    queryFn: async () => {
      const response = await subscriptionApi.getCurrentSubscription()
      return response.data
    },
    onError: () => {
      toast.error('Failed to load subscription')
    }
  })

  // Fetch available plans
  const { data: plansData, isLoading: plansLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const response = await subscriptionApi.getPlans()
      return response.data
    }
  })

  // Interval detector
  const detectInterval = (plan) => {
    if (!plan) return 'monthly'
    // Check interval field first (from API)
    if (plan.interval) {
      return plan.interval.toLowerCase()
    }
    // Fallback to name detection
    const name = (plan.name || '').toLowerCase()
    if (name.includes('year') || name.includes('annual')) {
      return 'yearly'
    }
    return 'monthly'
  }

  // Filter plans by billing cycle
  const monthlyPlans = plansData?.filter((p) => detectInterval(p) === 'monthly') || []
  const yearlyPlans = plansData?.filter((p) => detectInterval(p) === 'yearly') || []
  const visiblePlans = billingCycle === 'monthly' ? monthlyPlans : yearlyPlans

  // Upgrade/Downgrade mutation
  const upgradeMutation = useMutation({
    mutationFn: ({ plan, session }) => authApi.upgradeSubscription(plan, session),
    onSuccess: (data) => {
      // Close dialog first to prevent multiple triggers
      setConfirmDialogOpen(false)
      setSelectedPlan(null)
      
      // Show success message
      toast.success(data.data.message || 'Subscription updated successfully!')
      
      // Invalidate all subscription-related queries
      queryClient.invalidateQueries(['currentSubscription'])
      queryClient.invalidateQueries(['subscriptionPlans'])
      queryClient.invalidateQueries(['userProfile'])
      
      // Refetch after a small delay to ensure backend is ready
      setTimeout(() => {
        refetch()
      }, 500)
    },
    onError: (error) => {
      setConfirmDialogOpen(false)
      setSelectedPlan(null)
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to update subscription')
    }
  })

  // Cancel subscription mutation
  const cancelMutation = useMutation({
    mutationFn: (subscriptionId) => {
      // Using the existing cancel endpoint
      return authApi.cancelSubscription(subscriptionId)
    },
    onSuccess: (data) => {
      const endDate = data.data?.end_date
      const message = endDate 
        ? `Subscription will be cancelled on ${new Date(endDate).toLocaleDateString()}`
        : 'Subscription will be cancelled at the end of the billing period'
      
      toast.success(message)
      queryClient.invalidateQueries(['currentSubscription'])
      setCancelDialogOpen(false)
      
      // Refetch to show updated status
      setTimeout(() => {
        refetch()
      }, 500)
    },
    onError: (error) => {
      setCancelDialogOpen(false)
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to cancel subscription')
    }
  })

  const currentPlan = subscriptionData?.plan
  const sessionId = subscriptionData?.stripe_checkout_session_id
  const isCancelled = subscriptionData?.subscription_cancelled || subscriptionData?.payment_status === 'cancel'
  const endDate = subscriptionData?.end_date
  
  // Detect current plan's interval
  const currentInterval = detectInterval(currentPlan)
  const currentIntervalDisplay = currentInterval === 'yearly' ? 'Yearly' : 'Monthly'
  const currentIntervalShort = currentInterval === 'yearly' ? '/yr' : '/mo'

  const handleChangePlan = () => {
    if (selectedPlan && sessionId) {
      // Pass the plan alias instead of name to preserve interval information
      upgradeMutation.mutate({
        plan: selectedPlan.alias,
        session: sessionId
      })
      // Don't close dialog here - let mutation callbacks handle it
    }
  }

  const handleCancelSubscription = () => {
    if (subscriptionData?.stripe_subscription_id) {
      cancelMutation.mutate(subscriptionData.stripe_subscription_id)
    }
  }

  const handleResubscribe = async (plan) => {
    setResubscribeLoading(true)
    try {
      // Use dedicated resubscribe endpoint for cancelled subscriptions
      const response = await subscriptionApi.resubscribe(plan.alias)
      
      if (response.data.redirect_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.redirect_url
      } else if (response.data.has_access) {
        toast.success('Subscription activated!')
        setResubscribeDialogOpen(false)
        refetch()
      }
    } catch (error) {
      // If no cancelled subscription, fallback to normal flow
      if (error.response?.data?.use_normal_flow) {
        try {
          const fallbackResponse = await subscriptionApi.initPayment(plan.alias)
          if (fallbackResponse.data.redirect_url) {
            window.location.href = fallbackResponse.data.redirect_url
          }
        } catch (fallbackError) {
          toast.error('Failed to initialize payment')
          setResubscribeLoading(false)
        }
      } else {
        toast.error(error.response?.data?.error || 'Failed to resubscribe')
        setResubscribeLoading(false)
      }
    }
  }

  if (subLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!subscriptionData || !subscriptionData.has_access) {
    return (
      <div className="w-full">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Active Subscription</h3>
          <p className="text-gray-600 mb-4">Subscribe to access premium features</p>
          <Button onClick={() => window.location.href = '/pricing'}>
            View Plans
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <CreditCard className="w-8 h-8 text-primary" />
          Subscription
        </h1>
        <p className="text-gray-600">Manage your subscription and billing</p>
      </div>

      {/* Current Plan Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="w-6 h-6 text-white" />
              <div>
                <h2 className="text-xl font-bold text-white">{currentPlan.name} Plan</h2>
                <p className="text-slate-100 text-sm">
                  {isCancelled ? 'Cancelled - Active until end date' : 'Your current subscription'}
                </p>
              </div>
            </div>
            <Badge className={isCancelled ? "bg-orange-500 text-white" : "bg-green-500 text-white"}>
              {isCancelled ? 'Cancelled' : 'Active'}
            </Badge>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 pb-6 border-b border-gray-200">
            <div>
              <div className="text-sm text-gray-600 mb-1">Current Price</div>
              <p className="text-2xl font-bold text-gray-900">${currentPlan.price}<span className="text-base text-gray-600 font-normal">{currentIntervalShort}</span></p>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">Billing Cycle</div>
              <p className="text-lg font-semibold text-gray-900">{currentIntervalDisplay}</p>
            </div>
            {subscriptionData?.end_date && (
              <div>
                <div className="text-sm text-gray-600 mb-1">
                  {isCancelled ? 'Access Ends' : 'Next Billing'}
                </div>
                <p className={`text-lg font-semibold ${isCancelled ? 'text-orange-600' : 'text-gray-900'}`}>
                  {new Date(subscriptionData.end_date).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Cancelled Subscription Warning */}
          {isCancelled && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-orange-900 mb-1">Subscription Cancelled</h4>
                  <p className="text-sm text-orange-800">
                    Your subscription has been cancelled. You'll continue to have access until{' '}
                    <span className="font-semibold">
                      {new Date(endDate).toLocaleDateString('en-US', { 
                        month: 'long', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>.
                    After that, your account will revert to the free plan.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            {isCancelled ? (
              <Button 
                variant="default" 
                className="flex-1 bg-primary hover:bg-primary/90"
                onClick={() => setResubscribeDialogOpen(true)}
              >
                Resubscribe
              </Button>
            ) : (
              <Button 
                variant="outline" 
                className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                onClick={() => setCancelDialogOpen(true)}
              >
                Cancel Subscription
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Available Plans Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Available Plans</h2>
          <p className="text-sm text-gray-600">Choose a plan that fits your needs</p>
        </div>

        {/* Billing Cycle Toggle */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-lg font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-primary text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-lg font-medium transition-all ${
                billingCycle === 'yearly'
                  ? 'bg-primary text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Yearly
              <span className="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        <div className="p-6">

          {plansLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className={`grid gap-6 p-5 ${
              visiblePlans.length === 1 
                ? 'grid-cols-1 max-w-md mx-auto' 
                : visiblePlans.length === 2 
                ? 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto' 
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            }`}>
              {visiblePlans.length > 0 ? (
                visiblePlans
                  .sort((a, b) => parseFloat(a.price) - parseFloat(b.price))
                  .map((plan) => {
                  const currentPrice = parseFloat(currentPlan?.price || 0)
                  const planPrice = parseFloat(plan.price)
                  const isCurrentPlan = currentPlan?.alias === plan.alias
                  const isUpgrade = planPrice > currentPrice
                  const isDowngrade = planPrice < currentPrice
                  const isSelected = selectedPlan?.alias === plan.alias

                  // Determine tier for styling
                  const tierStyle = planPrice === 0 
                    ? 'border-gray-300 bg-gray-50'
                    : planPrice < 10
                    ? 'border-slate-300 bg-slate-50'
                    : 'border-slate-400 bg-slate-100'

                  return (
                    <div
                      key={plan.alias}
                      className={`relative border-2 rounded-2xl overflow-hidden transition-all duration-300 ${
                        isSelected
                          ? 'border-primary shadow-2xl scale-105'
                          : isCurrentPlan
                          ? 'border-green-600 shadow-lg'
                          : 'border-gray-200 hover:border-gray-400 hover:shadow-xl'
                      } ${
                        !isCurrentPlan ? 'cursor-pointer' : ''
                      }`}
                      onClick={() => {
                        if (!isCurrentPlan) {
                          setSelectedPlan(plan)
                          setConfirmDialogOpen(true)
                        }
                      }}
                    >
                      {/* Header */}
                      <div className={`px-6 py-4 ${
                        isCurrentPlan
                          ? 'bg-gradient-to-r from-green-500 to-green-600'
                          : isSelected
                          ? 'bg-gradient-to-r from-slate-700 to-slate-800'
                          : tierStyle
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <h3 className={`text-xl font-bold ${
                            isCurrentPlan || isSelected ? 'text-white' : 'text-gray-900'
                          }`}>
                            {plan.name}
                          </h3>
                          {isCurrentPlan && (
                            <Badge className="bg-white text-green-600">Current</Badge>
                          )}
                          {!isCurrentPlan && isUpgrade && (
                            <div className={`flex items-center gap-1 text-sm font-semibold ${
                              isSelected ? 'text-white' : 'text-primary'
                            }`}>
                              <ArrowUp className="w-4 h-4" />
                              Upgrade
                            </div>
                          )}
                          {!isCurrentPlan && isDowngrade && (
                            <div className={`flex items-center gap-1 text-sm font-semibold ${
                              isSelected ? 'text-white' : 'text-orange-600'
                            }`}>
                              <ArrowDown className="w-4 h-4" />
                              Downgrade
                            </div>
                          )}
                        </div>
                        <div className={`text-4xl font-bold ${
                          isCurrentPlan || isSelected ? 'text-white' : 'text-gray-900'
                        }`}>
                          ${plan.price}
                          <span className={`text-lg font-normal ${
                            isCurrentPlan || isSelected ? 'text-white/80' : 'text-gray-600'
                          }`}>/{detectInterval(plan) === 'yearly' ? 'yr' : 'mo'}</span>
                        </div>
                        {detectInterval(plan) === 'yearly' && (
                          <span className="inline-block mt-2 rounded-full bg-green-500 px-3 py-1 text-xs font-semibold text-white">
                            Save 20%
                          </span>
                        )}
                      </div>

                      {/* Body */}
                      <div className="p-6 bg-white">
                        {plan.features && plan.features.length > 0 ? (
                          <div className="space-y-3 mb-6">
                            {plan.features.map((feature, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                                <span className="text-sm text-gray-700">{feature.inline_feature}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 italic mb-6">
                            No features listed
                          </div>
                        )}

                        {isCurrentPlan && (
                          <div className="text-center py-3 bg-green-50 rounded-lg">
                            <span className="text-sm font-medium text-green-700">
                              ✓ Your Active Plan
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Hover Effect */}
                      {!isCurrentPlan && (
                        <div className="absolute inset-0 bg-primary/0 hover:bg-primary/5 transition-all pointer-events-none" />
                      )}
                    </div>
                  )
                })
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No {billingCycle} plans available
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Confirm Plan Change</DialogTitle>
            <DialogDescription>
              Review your plan change before confirming.
            </DialogDescription>
          </DialogHeader>

          {selectedPlan && (
            <div className="space-y-6">
              {/* Plan Comparison */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Current Plan</span>
                  <span className="font-semibold text-gray-900">{currentPlan?.name} - ${currentPlan?.price}/mo</span>
                </div>
                
                <div className="flex items-center justify-center">
                  {parseFloat(selectedPlan.price) > parseFloat(currentPlan?.price || 0) ? (
                    <div className="flex items-center gap-2 text-primary">
                      <ArrowUp className="w-4 h-4" />
                      <span className="text-sm font-medium">Upgrading</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-orange-600">
                      <ArrowDown className="w-4 h-4" />
                      <span className="text-sm font-medium">Downgrading</span>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">New Plan</span>
                  <span className="font-semibold text-primary">{selectedPlan.name} - ${selectedPlan.price}/{detectInterval(selectedPlan) === 'yearly' ? 'yr' : 'mo'}</span>
                </div>
              </div>

              {/* Important Note */}
              <div className={`rounded-lg p-4 border ${
                parseFloat(selectedPlan.price) > parseFloat(currentPlan?.price || 0)
                  ? 'bg-slate-50 border-slate-200'
                  : 'bg-orange-50 border-orange-200'
              }`}>
                <div className="flex gap-3">
                  <AlertCircle className={`w-5 h-5 flex-shrink-0 ${
                    parseFloat(selectedPlan.price) > parseFloat(currentPlan?.price || 0)
                      ? 'text-primary'
                      : 'text-orange-600'
                  }`} />
                  <div>
                    <p className={`text-sm font-medium mb-1 ${
                      parseFloat(selectedPlan.price) > parseFloat(currentPlan?.price || 0)
                        ? 'text-slate-900'
                        : 'text-orange-900'
                    }`}>
                      {parseFloat(selectedPlan.price) > parseFloat(currentPlan?.price || 0)
                        ? 'Immediate Upgrade'
                        : 'Scheduled Downgrade'}
                    </p>
                    <p className={`text-sm ${
                      parseFloat(selectedPlan.price) > parseFloat(currentPlan?.price || 0)
                        ? 'text-slate-800'
                        : 'text-orange-800'
                    }`}>
                      {parseFloat(selectedPlan.price) > parseFloat(currentPlan?.price || 0)
                        ? 'Changes take effect immediately. You\'ll be charged the prorated amount.'
                        : 'Changes take effect at the end of your billing period. No immediate charges.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setConfirmDialogOpen(false)
                    setSelectedPlan(null)
                  }}
                  disabled={upgradeMutation.isLoading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleChangePlan}
                  disabled={upgradeMutation.isLoading}
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  {upgradeMutation.isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Confirm'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel your subscription? You'll continue to have access until the end of your current billing period.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelSubscription}
              className="bg-red-600 hover:bg-red-700"
              disabled={cancelMutation.isLoading}
            >
              {cancelMutation.isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Yes, Cancel Subscription'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resubscribe Dialog */}
      <Dialog open={resubscribeDialogOpen} onOpenChange={setResubscribeDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Resubscribe to Premium</DialogTitle>
            <DialogDescription>
              Choose a plan to reactivate your subscription
            </DialogDescription>
          </DialogHeader>

          {/* Billing Cycle Toggle */}
          <div className="flex items-center justify-center gap-4 my-4">
            <Button
              variant={billingCycle === 'monthly' ? 'default' : 'outline'}
              onClick={() => setBillingCycle('monthly')}
              size="sm"
            >
              Monthly
            </Button>
            <Button
              variant={billingCycle === 'yearly' ? 'default' : 'outline'}
              onClick={() => setBillingCycle('yearly')}
              size="sm"
            >
              Yearly
              <Badge className="ml-2 bg-green-500">Save 20%</Badge>
            </Button>
          </div>

          {/* Plans Grid - Centered when less than 3 plans */}
          <div className={`grid gap-6 p-5 ${
            visiblePlans.length === 1 
              ? 'grid-cols-1 max-w-md mx-auto' 
              : visiblePlans.length === 2 
              ? 'grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto' 
              : 'grid-cols-1 md:grid-cols-3'
          }`}>
            {visiblePlans
              .sort((a, b) => parseFloat(a.price) - parseFloat(b.price))
              .map((plan) => {
                const planInterval = detectInterval(plan)
                const intervalDisplay = planInterval === 'yearly' ? '/year' : '/month'
                
                return (
                  <div
                    key={plan.alias}
                    className="border rounded-lg p-4 hover:border-primary transition-colors cursor-pointer"
                    onClick={() => !resubscribeLoading && handleResubscribe(plan)}
                  >
                    <h3 className="text-lg font-bold mb-2">{plan.name}</h3>
                    <div className="text-3xl font-bold mb-4">
                      ${plan.price}
                      <span className="text-sm text-gray-600">{intervalDisplay}</span>
                    </div>
                    <ul className="space-y-2 mb-4">
                      {plan.features?.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>{feature.inline_feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      className="w-full"
                      disabled={resubscribeLoading}
                    >
                      {resubscribeLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        'Select Plan'
                      )}
                    </Button>
                  </div>
                )
              })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
